/**
 * The eight artifacts of the "Explain a dev process" demo: a database migration told to the whole
 * company, not only to engineers.
 *
 * Styled after Supabase's own site: a near black ground, cards with hairline borders, Manrope
 * headlines, Source Code Pro for anything technical, quiet line art on fine grids, and green for the
 * new database. Why first, then the plan, then one page per step that always ends on what customers
 * see, and last the way back.
 */

import { CODE, MANROPE, circle, frame, line, rect, text, wrap, type DemoSlide } from './slideSvg';

/** Hairline white at a given strength: every border, grid and connector is drawn this way. */
const white = (a: number) => `rgba(242,242,242,${a})`;

const C = {
  ground: '#131413',
  card: '#181A19',
  dialog: '#1B1D1C',
  raised: '#1E201F',
  border: white(0.07),
  strong: white(0.13),
  text: '#EDEFEE',
  light: '#BCBDBC',
  muted: '#989A99',
  /** Neutral line art: the old database and the connectors that are not Postgres. */
  art: '#6E706F',
  green: '#3ECF8E',
  greenBorder: 'rgba(63,209,142,0.3)',
  greenFill: 'rgba(62,207,142,0.12)',
  greenDeep: '#006338',
  amber: '#F2AF48',
  red: '#B54A46',
} as const;

const LEFT = 64;
const RIGHT = 1216;
const WIDTH = RIGHT - LEFT;
/** Cards keep 40 px inside their border. */
const IN_L = LEFT + 40;
const IN_R = RIGHT - 40;
/** Every content page puts its headline on this baseline. */
const HEADLINE_Y = 164;
/** Where the content card starts, below a headline alone or a headline with its supporting line. */
const CARD_Y = 212;
const CARD_Y_SUPPORT = 252;
/** Content ends 64 px off the bottom edge, like the sides; the steps keep the last row for their strip. */
const BOTTOM = 656;
const STRIP_Y = 592;
const STRIP_H = BOTTOM - STRIP_Y;
/** Charts share one data column inside their card. */
const DATA_X = 248;
const DATA_END = 1072;

const r2 = (v: number) => Math.round(v * 100) / 100;

/* ------------------------------------------------------------------------------------------------ */
/* The page frame                                                                                    */
/* ------------------------------------------------------------------------------------------------ */

/** Ground, running line and page number: the frame every content page shares. */
function page(n: number, content: string): string {
  return wrap(
    rect(0, 0, 1280, 720, C.ground) +
      text(LEFT, 52, 13, 'Moving to Postgres', { fill: C.muted, font: CODE, tracking: '0' }) +
      text(RIGHT, 52, 13, String(n).padStart(2, '0'), { fill: C.muted, font: CODE, anchor: 'end', tracking: '0' }) +
      content,
  );
}

const headline = (str: string) => text(LEFT, HEADLINE_Y, 48, str, { fill: C.text, font: MANROPE, weight: 500, tracking: '0' });

/**
 * The supporting line under a headline, in Supabase's manner: grey, with its key phrase in white.
 * `lead` is where the key phrase starts: the grey part's advance plus one space, measured in Inter 22
 * with the face itself, so it has to be measured again if either part's words change.
 */
function support(grey: string, key: string, lead: number): string {
  return text(LEFT, 206, 22, grey, { fill: C.muted, tracking: '0' }) + text(LEFT + lead, 206, 22, key, { fill: C.text, tracking: '0' });
}

/** "Step 2 of 4" over the headline, with the four steps as short ticks. */
function stepTag(n: number): string {
  const ticks = [1, 2, 3, 4]
    .map((i) => rect(LEFT + 118 + (i - 1) * 26, 102, 20, 3, i === n ? C.green : i < n ? C.muted : C.strong, 1.5))
    .join('');
  return text(LEFT, 108, 13, `STEP ${n} OF 4`, { fill: C.green, font: CODE, tracking: '0.08em' }) + ticks;
}

/* ------------------------------------------------------------------------------------------------ */
/* Surfaces, icons and line art                                                                      */
/* ------------------------------------------------------------------------------------------------ */

function card(x: number, y: number, w: number, h: number, fill: string = C.card, r = 16): string {
  return rect(x, y, w, h, fill, r) + frame(x, y, w, h, C.border, r);
}

/** A 16 px line icon: `d` is drawn on a 16 unit box with its top left at (x, y). */
function icon(d: string, x: number, y: number, color: string): string {
  return `<path d="${d}" transform="translate(${x} ${y})" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`;
}

const ICON = {
  eye: 'M1.5 8C3.4 4.3 12.6 4.3 14.5 8C12.6 11.7 3.4 11.7 1.5 8Z M6.1 8A1.9 1.9 0 1 0 9.9 8A1.9 1.9 0 1 0 6.1 8Z',
  moon: 'M8 2a4 4 0 0 0 6 6a6 6 0 1 1-6-6Z',
  table: 'M3 2.5h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Z M2 6.5h12 M6.5 6.5v7',
  check: 'M3 8.5l3.2 3.2L13 4.8',
  database:
    'M2.5 4a5.5 2 0 1 0 11 0a5.5 2 0 1 0-11 0Z M2.5 4v8c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2V4 M2.5 8c0 1.1 2.46 2 5.5 2s5.5-.9 5.5-2',
};

/**
 * A fine grid inside a card, 1 px lines through (ox, oy) that fade out toward the box's edges.
 * `id` must be unique across every slide, since slides can sit inline on one page. The lines stay
 * faint enough that the morph's reading order (`partsMapFrom`) does not count them as ink, so the
 * drawing on top still resolves piece by piece.
 */
function fineGrid(id: string, x: number, y: number, w: number, h: number, ox: number, oy: number, cell = 32): string {
  const first = (o: number, lo: number) => o - Math.floor((o - lo) / cell) * cell;
  let d = '';
  for (let gx = first(ox, x); gx <= x + w; gx += cell) d += `M ${gx + 0.5} ${y} V ${y + h} `;
  for (let gy = first(oy, y); gy <= y + h; gy += cell) d += `M ${x} ${gy + 0.5} H ${x + w} `;
  return (
    `<defs><radialGradient id="${id}-fade" cx="0.5" cy="0.5" r="0.5"><stop offset="0.25" stop-color="#fff"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>` +
    `<mask id="${id}-mask" maskUnits="userSpaceOnUse" x="${x}" y="${y}" width="${w}" height="${h}"><rect x="${x}" y="${y}" width="${w}" height="${h}" fill="url(#${id}-fade)"/></mask></defs>` +
    `<path d="${d.trim()}" fill="none" stroke="${white(0.045)}" stroke-width="1" mask="url(#${id}-mask)"/>`
  );
}

/** How tall a drum's lid ellipse is for its width: a gentle three-quarter view. */
const lidRy = (w: number) => Math.round(w * 0.15);

interface DrumStyle {
  stroke: string;
  fill: string;
  /** Set for a solid drum: the colour of the lid's rim and the bands drawn over the fill. */
  ink?: string;
  lid?: string;
  width?: number;
  opacity?: number;
}

/** A database drawn as a drum: a lid, two bands, one thin line weight. */
function drum(cx: number, top: number, w: number, h: number, s: DrumStyle): string {
  const rx = w / 2;
  const ry = lidRy(w);
  const x0 = cx - rx;
  const x1 = cx + rx;
  const bottom = top + h;
  const sw = s.width ?? 1.5;
  const front = (y: number) => `M ${x0} ${r2(y)} A ${rx} ${ry} 0 0 0 ${x1} ${r2(y)}`;
  const strokeOf = (d: string, color: string) => `<path d="${d}" fill="none" stroke="${color}" stroke-width="${sw}"/>`;
  const body = `<path d="M ${x0} ${top} A ${rx} ${ry} 0 0 1 ${x1} ${top} L ${x1} ${bottom} A ${rx} ${ry} 0 0 1 ${x0} ${bottom} Z" fill="${s.fill}"/>`;
  const bands = `${front(top + h / 3)} ${front(top + (2 * h) / 3)}`;
  const lid = `<ellipse cx="${cx}" cy="${top}" rx="${rx}" ry="${ry}" fill="${s.lid ?? s.fill}" stroke="${s.ink ?? s.stroke}" stroke-width="${sw}"/>`;
  const outline = strokeOf(`M ${x0} ${top} L ${x0} ${bottom} A ${rx} ${ry} 0 0 0 ${x1} ${bottom} L ${x1} ${top}`, s.stroke);
  const parts = s.ink ? body + outline + strokeOf(bands, s.ink) + lid : body + outline + strokeOf(bands, s.stroke) + lid;
  return s.opacity === undefined ? parts : `<g opacity="${s.opacity}">${parts}</g>`;
}

/** An open chevron with its tip at (x, y), pointing along `angle`. */
function chevron(x: number, y: number, angle: number, color: string): string {
  const len = 8;
  const spread = 0.62;
  const ax = r2(x - Math.cos(angle - spread) * len);
  const ay = r2(y - Math.sin(angle - spread) * len);
  const bx = r2(x - Math.cos(angle + spread) * len);
  const by = r2(y - Math.sin(angle + spread) * len);
  return `<path d="M ${ax} ${ay} L ${x} ${y} L ${bx} ${by}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`;
}

/** A connector: dashed like Supabase's API lines, or solid for the one that matters, ending in a chevron. */
function connector(d: string, tipX: number, tipY: number, angle: number, color: string, dashed: boolean): string {
  return (
    `<path d="${d}" fill="none" stroke="${color}" stroke-width="1.5"${dashed ? ' stroke-dasharray="3 5"' : ''} stroke-linecap="round" stroke-linejoin="round"/>` +
    chevron(tipX, tipY, angle, color)
  );
}

/** A green node dot with a soft halo, the one bright spot Supabase allows itself. */
const glowDot = (cx: number, cy: number) => circle(cx, cy, 7, 'rgba(62,207,142,0.16)') + circle(cx, cy, 3, C.green);

/** A mono pill sitting on a connector, like the endpoint pills on Supabase's API card. */
function codePill(cx: number, cy: number, label: string, color: string = C.light, border: string = C.strong): string {
  const w = Math.round(label.length * 7.2 + 26);
  const h = 26;
  const x = r2(cx - w / 2);
  return rect(x, cy - h / 2, w, h, C.raised, h / 2) + frame(x, cy - h / 2, w, h, border, h / 2) + text(cx, cy + 4.5, 12, label, { fill: color, font: CODE, anchor: 'middle', tracking: '0' });
}

/** A status pill under a label. */
function badge(cx: number, y: number, label: string, color: string = C.light): string {
  const w = Math.round(label.length * 7 + 30);
  const x = r2(cx - w / 2);
  return rect(x, y, w, 28, white(0.03), 14) + frame(x, y, w, 28, C.strong, 14) + text(cx, y + 18.5, 13, label, { fill: color, anchor: 'middle', tracking: '0' });
}

/** The app as a small window: raised surface, strong border, three chrome dots. */
function appWindow(cx: number, cy: number, w: number, h: number): string {
  const x = cx - w / 2;
  const y = cy - h / 2;
  return (
    rect(x, y, w, h, C.raised, 12) +
    frame(x, y, w, h, C.strong, 12) +
    [0, 1, 2].map((i) => circle(x + 14 + i * 9, y + 13, 2.5, white(0.22))).join('') +
    text(cx, cy + 7, 20, 'App', { fill: C.text, font: MANROPE, weight: 600, anchor: 'middle', tracking: '0' })
  );
}

/** The strip every step ends on, so people outside engineering always get their answer. */
function customers(answer: string): string {
  const mid = STRIP_Y + STRIP_H / 2;
  return (
    card(LEFT, STRIP_Y, WIDTH, STRIP_H, C.card, 12) +
    icon(ICON.eye, LEFT + 24, mid - 8, C.light) +
    text(LEFT + 52, mid + 5.5, 16, 'What customers see', { fill: C.light, font: MANROPE, weight: 600, tracking: '0' }) +
    line(LEFT + 252.5, STRIP_Y + 20, LEFT + 252.5, STRIP_Y + STRIP_H - 20, C.strong) +
    text(LEFT + 276, mid + 7, 20, answer, { fill: C.text, weight: 500, tracking: '-0.01em' })
  );
}

/* ------------------------------------------------------------------------------------------------ */
/* 1. Cover                                                                                          */
/* ------------------------------------------------------------------------------------------------ */

/** The drawing's card, 18 cells of 32 px a side; everything on it sits on the grid. */
const PLATE = { x: 640, y: 72, size: 576 };
const COVER_OLD = 800;
const COVER_APP = 928;
const COVER_NEW = 1056;
const COVER_BOX = { cy: 168, w: 192, h: 64 };
const COVER_JOIN = 264;
const COVER_DRUM = 128;
const COVER_LID = 360;
const COVER_TIP = COVER_LID - lidRy(COVER_DRUM) - 6;
const COVER_LABEL_Y = 542;

/** From the junction under the app, down into one drum, with a rounded corner. */
function branch(toX: number, color: string, dashed: boolean): string {
  const dir = toX < COVER_APP ? -1 : 1;
  const d = `M ${COVER_APP} ${COVER_JOIN} H ${toX - dir * 12} Q ${toX} ${COVER_JOIN} ${toX} ${COVER_JOIN + 12} V ${COVER_TIP}`;
  return connector(d, toX, COVER_TIP, Math.PI / 2, color, dashed);
}

/** How long the move takes, drawn as a dimension between the two databases. */
function dimension(x1: number, x2: number, y: number, label: string): string {
  return (
    line(x1, y + 0.5, x2, y + 0.5, white(0.2), 1, '2 4') +
    line(x1 + 0.5, y - 7, x1 + 0.5, y + 8, white(0.3)) +
    line(x2 + 0.5, y - 7, x2 + 0.5, y + 8, white(0.3)) +
    codePill((x1 + x2) / 2, y + 0.5, label)
  );
}

const cover = wrap(
  rect(0, 0, 1280, 720, C.ground) +
    card(PLATE.x, PLATE.y, PLATE.size, PLATE.size) +
    fineGrid('dev-grid-1', PLATE.x, PLATE.y, PLATE.size, PLATE.size, PLATE.x, PLATE.y) +
    `<path d="M ${COVER_APP} ${COVER_BOX.cy + COVER_BOX.h / 2} V ${COVER_JOIN}" fill="none" stroke="${white(0.35)}" stroke-width="1.5" stroke-dasharray="3 5" stroke-linecap="round"/>` +
    branch(COVER_OLD, C.art, true) +
    branch(COVER_NEW, C.green, false) +
    appWindow(COVER_APP, COVER_BOX.cy, COVER_BOX.w, COVER_BOX.h) +
    circle(COVER_APP, COVER_JOIN, 3.5, C.text) +
    drum(COVER_OLD, COVER_LID, COVER_DRUM, COVER_DRUM, { stroke: C.art, fill: C.card, width: 1.25 }) +
    drum(COVER_NEW, COVER_LID, COVER_DRUM, COVER_DRUM, { stroke: C.green, fill: C.card }) +
    glowDot(COVER_NEW, COVER_LID) +
    text(COVER_OLD, COVER_LABEL_Y, 16, 'Old database', { fill: C.muted, anchor: 'middle', tracking: '0' }) +
    text(COVER_NEW, COVER_LABEL_Y, 16, 'Postgres', { fill: C.green, anchor: 'middle', tracking: '0' }) +
    dimension(COVER_OLD, COVER_NEW, COVER_LABEL_Y + 50, '6 weeks') +
    text(LEFT, 314, 88, 'Moving to', { fill: C.text, font: MANROPE, weight: 500, tracking: '0' }) +
    text(LEFT, 406, 88, 'Postgres', { fill: C.green, font: MANROPE, weight: 500, tracking: '0' }) +
    text(LEFT, 470, 18, 'Platform team · October', { fill: C.muted, tracking: '0' }),
);

/* ------------------------------------------------------------------------------------------------ */
/* 2. Why we are moving                                                                              */
/* ------------------------------------------------------------------------------------------------ */

const PX_PER_SECOND = (DATA_END - DATA_X) / 40;

function reportChart(): string {
  const top = 316;
  const axisY = 580;
  const ticks = [0, 10, 20, 30, 40]
    .map((s) => {
      const x = DATA_X + s * PX_PER_SECOND;
      return (
        line(x + 0.5, top, x + 0.5, axisY, s === 0 ? C.strong : C.border, 1, s === 0 ? undefined : '3 4') +
        text(x, axisY + 26, 12, s === 40 ? '40 s' : String(s), { fill: C.muted, font: CODE, anchor: 'middle', tracking: '0' })
      );
    })
    .join('');
  const bar = (y: number, label: string, seconds: number, color: string, value: string) =>
    text(IN_L, y + 34, 18, label, { fill: C.text, tracking: '0' }) +
    rect(DATA_X, y, r2(seconds * PX_PER_SECOND), 56, color, 6) +
    text(DATA_X + seconds * PX_PER_SECOND + 20, y + 40, 32, value, { fill: C.text, font: MANROPE, weight: 500, tracking: '0' });
  return (
    card(LEFT, CARD_Y_SUPPORT, WIDTH, BOTTOM - CARD_Y_SUPPORT) +
    ticks +
    line(DATA_X, axisY + 0.5, DATA_END, axisY + 0.5, C.strong) +
    bar(340, 'Today', 40, C.red, '40 s') +
    bar(452, 'On Postgres', 1.8, C.green, '1.8 s')
  );
}

const why = page(2, headline('Month-end reports take 40 seconds.') + support('Same report, same data.', 'Only the database changes.', 260.76) + reportChart());

/* ------------------------------------------------------------------------------------------------ */
/* 3. The plan                                                                                       */
/* ------------------------------------------------------------------------------------------------ */

const WEEK = 124;
const GANTT_X = IN_R - 6 * WEEK;
const WEEK_DATES = ['Oct 5', 'Oct 12', 'Oct 19', 'Oct 26', 'Nov 2', 'Nov 9'];
const STEPS = [
  { name: 'Write to both', start: 0, weeks: 1 },
  { name: 'Copy the past', start: 1, weeks: 2 },
  { name: 'Read from the new one', start: 3, weeks: 2 },
  { name: 'Switch off the old one', start: 5, weeks: 1 },
];

/** A step's bar: done in green, running in amber, still to come as a quiet outline. */
function stepBar(x: number, y: number, w: number, state: 'done' | 'running' | 'next'): string {
  const h = 28;
  if (state === 'done') return rect(x, y, w, h, C.greenDeep, 8) + frame(x, y, w, h, C.greenBorder, 8);
  if (state === 'running') return rect(x, y, w, h, 'rgba(242,175,72,0.16)', 8) + frame(x, y, w, h, 'rgba(242,175,72,0.55)', 8);
  return rect(x, y, w, h, white(0.03), 8) + frame(x, y, w, h, C.strong, 8);
}

function gantt(): string {
  const cardY = CARD_Y;
  const head = cardY + 40;
  const top = head + 60;
  const row = 64;
  const bottom = top + row * STEPS.length;
  const columns = [0, 1, 2, 3, 4, 5, 6].map((i) => line(GANTT_X + i * WEEK + 0.5, head, GANTT_X + i * WEEK + 0.5, bottom, C.border)).join('');
  const rows = [0, 1, 2, 3, 4].map((i) => line(IN_L, top + i * row + 0.5, IN_R, top + i * row + 0.5, i === 0 ? C.strong : C.border)).join('');
  const header = WEEK_DATES.map(
    (date, i) =>
      text(GANTT_X + i * WEEK + 12, head + 22, 14, `Week ${i + 1}`, { fill: C.text, tracking: '0' }) +
      text(GANTT_X + i * WEEK + 12, head + 42, 12, date, { fill: C.muted, font: CODE, tracking: '0' }),
  ).join('');
  // Today is in week 3: step 1 is done and the copy is running, which is what the step pages show.
  const today = GANTT_X + WEEK * 2.4;
  const pillW = 76;
  const marker =
    line(today, top - 8, today, bottom + 14, white(0.55), 1) +
    circle(today, top - 8, 3, C.text) +
    rect(today - pillW / 2, bottom + 14, pillW, 26, C.raised, 13) +
    frame(today - pillW / 2, bottom + 14, pillW, 26, C.strong, 13) +
    circle(today - pillW / 2 + 14, bottom + 27, 3, C.green) +
    text(today + 8, bottom + 31.5, 12, 'Today', { fill: C.text, font: CODE, anchor: 'middle', tracking: '0' });
  const bars = STEPS.map((s, i) => {
    const y = top + i * row;
    const state = i === 0 ? 'done' : i === 1 ? 'running' : 'next';
    return (
      text(IN_L, y + 37, 12, `0${i + 1}`, { fill: C.muted, font: CODE, tracking: '0' }) +
      text(IN_L + 32, y + 38, 18, s.name, { fill: C.text, tracking: '0' }) +
      stepBar(GANTT_X + s.start * WEEK + 6, y + 18, s.weeks * WEEK - 12, state)
    );
  }).join('');
  return card(LEFT, cardY, WIDTH, BOTTOM - cardY) + columns + rows + header + bars + marker;
}

const plan = page(3, headline('Four steps. Six weeks.') + gantt());

/* ------------------------------------------------------------------------------------------------ */
/* 4 to 7. The steps                                                                                 */
/* ------------------------------------------------------------------------------------------------ */

/** One layout for every page that shows the databases: old left, the app in the middle, new right. */
const OLD_X = 232;
const APP_X = 640;
const NEW_X = 1048;
const MID_Y = 350;
const APP_W = 160;
const APP_H = 72;
const DRUM_W = 160;
const DRUM_H = 132;
const DRUM_TOP = MID_Y - DRUM_H / 2;
const LABEL_Y = MID_Y + 138;
const OLD_EDGE = OLD_X + DRUM_W / 2 + 10;
const NEW_EDGE = NEW_X - DRUM_W / 2 - 10;
const APP_LEFT = APP_X - APP_W / 2;
const APP_RIGHT = APP_X + APP_W / 2;
const DIAGRAM_H = STRIP_Y - 16 - CARD_Y;

/** The card the database drawing sits on, with its fine grid centred on the app. */
const diagramCard = (id: string) => card(LEFT, CARD_Y, WIDTH, DIAGRAM_H) + fineGrid(id, LEFT, CARD_Y, WIDTH, DIAGRAM_H, APP_X, MID_Y);

const writeBoth = page(
  4,
  stepTag(1) +
    headline('Every save goes to both.') +
    diagramCard('dev-grid-4') +
    connector(`M ${APP_LEFT} ${MID_Y} H ${OLD_EDGE}`, OLD_EDGE, MID_Y, Math.PI, C.light, true) +
    connector(`M ${APP_RIGHT} ${MID_Y} H ${NEW_EDGE}`, NEW_EDGE, MID_Y, 0, C.light, true) +
    codePill((APP_LEFT + OLD_EDGE) / 2, MID_Y, 'save') +
    codePill((APP_RIGHT + NEW_EDGE) / 2, MID_Y, 'save') +
    appWindow(APP_X, MID_Y, APP_W, APP_H) +
    drum(OLD_X, DRUM_TOP, DRUM_W, DRUM_H, { stroke: C.art, fill: C.card, width: 1.25 }) +
    drum(NEW_X, DRUM_TOP, DRUM_W, DRUM_H, { stroke: C.green, fill: C.card }) +
    glowDot(NEW_X, DRUM_TOP) +
    text(OLD_X, LABEL_Y, 18, 'Old database', { fill: C.muted, anchor: 'middle', tracking: '0' }) +
    badge(OLD_X, LABEL_Y + 14, 'still the source') +
    text(NEW_X, LABEL_Y, 18, 'Postgres', { fill: C.green, anchor: 'middle', tracking: '0' }) +
    customers('Nothing.'),
);

const TABLES: [string, number][] = [
  ['users', 1],
  ['orders', 0.72],
  ['invoices', 0.45],
  ['events', 0.18],
];

/** The night tag: a crescent and the hours the copy runs. */
function nightTag(y: number): string {
  const label = 'Runs 01:00 to 05:00';
  const w = Math.round(label.length * 7.8 + 60);
  const x = IN_R - w;
  return (
    rect(x, y, w, 32, C.raised, 16) +
    frame(x, y, w, 32, C.strong, 16) +
    icon(ICON.moon, x + 14, y + 8, C.light) +
    text(x + 40, y + 20.5, 13, label, { fill: C.text, font: CODE, tracking: '0' })
  );
}

/** A table name in a pill with the table glyph, as Supabase lists them. */
function tablePill(x: number, cy: number, name: string): string {
  const w = Math.round(name.length * 7.8 + 46);
  return (
    rect(x, cy - 15, w, 30, white(0.03), 8) +
    frame(x, cy - 15, w, 30, C.strong, 8) +
    icon(ICON.table, x + 10, cy - 8, C.muted) +
    text(x + 34, cy + 4.5, 13, name, { fill: C.text, font: CODE, tracking: '0' })
  );
}

function copyRows(): string {
  const tw = DATA_END - DATA_X;
  return TABLES.map(([name, p], i) => {
    const cy = 324 + i * 58;
    const done = p === 1;
    return (
      tablePill(IN_L, cy, name) +
      rect(DATA_X, cy - 5, tw, 10, C.raised, 5) +
      frame(DATA_X, cy - 5, tw, 10, C.border, 5) +
      rect(DATA_X, cy - 5, r2(tw * p), 10, done ? C.green : C.amber, 5) +
      text(IN_R, cy + 6, 16, `${Math.round(p * 100)}%`, { fill: done ? C.green : C.text, font: CODE, anchor: 'end', tracking: '0' })
    );
  }).join('');
}

const copyPast = page(
  5,
  stepTag(2) +
    headline('Six years of data, copied at night.') +
    card(LEFT, CARD_Y, WIDTH, DIAGRAM_H) +
    nightTag(CARD_Y + 28) +
    copyRows() +
    customers('Exports a little slower at night.'),
);

const STAGES: [string, number][] = [
  ['Oct 26', 1],
  ['Oct 29', 10],
  ['Nov 2', 20],
];

/** Each stage as 20 cells, one per 5% of reads: green on Postgres, empty on the old database. */
function splitRows(): string {
  const pitch = (DATA_END - DATA_X + 6) / 20;
  const cw = r2(pitch - 6);
  const ch = 40;
  return STAGES.map(([date, on], i) => {
    const y = 296 + i * 68;
    const cells = Array.from({ length: 20 }, (_, k) => {
      const x = r2(DATA_X + k * pitch);
      return k < on ? rect(x, y, cw, ch, C.greenDeep, 6) + frame(x, y, cw, ch, 'rgba(63,209,142,0.55)', 6) : rect(x, y, cw, ch, C.raised, 6) + frame(x, y, cw, ch, C.border, 6);
    }).join('');
    return (
      text(IN_L, y + 26, 18, date, { fill: C.text, tracking: '0' }) +
      cells +
      text(IN_R, y + 31, 30, `${on * 5}%`, { fill: C.green, font: MANROPE, weight: 500, anchor: 'end', tracking: '0' })
    );
  }).join('');
}

function splitLegend(): string {
  const y = 528;
  return (
    rect(DATA_X, y - 11, 14, 14, C.greenDeep, 4) +
    frame(DATA_X, y - 11, 14, 14, 'rgba(63,209,142,0.55)', 4) +
    text(DATA_X + 24, y + 1, 13, 'Reads on Postgres', { fill: C.muted, tracking: '0' }) +
    rect(DATA_X + 184, y - 11, 14, 14, C.raised, 4) +
    frame(DATA_X + 184, y - 11, 14, 14, C.strong, 4) +
    text(DATA_X + 208, y + 1, 13, 'Reads on the old database', { fill: C.muted, tracking: '0' })
  );
}

const readNew = page(
  6,
  stepTag(3) +
    headline('Reads move over slowly.') +
    support('Every answer is', 'checked against the old one.', 170.16) +
    card(LEFT, CARD_Y_SUPPORT, WIDTH, STRIP_Y - 16 - CARD_Y_SUPPORT) +
    splitRows() +
    splitLegend() +
    customers('Faster reports.'),
);

/** A connection that is no longer there: dashed, with a break in the middle. */
function cut(x1: number, x2: number, y: number): string {
  const mid = (x1 + x2) / 2;
  return (
    line(x1, y, mid - 12, y, white(0.14), 1.5, '3 5') +
    line(mid + 12, y, x2, y, white(0.14), 1.5, '3 5') +
    line(mid - 9, y + 9, mid - 3, y - 9, C.muted, 1.5) +
    line(mid + 3, y + 9, mid + 9, y - 9, C.muted, 1.5)
  );
}

const retire = page(
  7,
  stepTag(4) +
    headline('The old one retires.') +
    diagramCard('dev-grid-7') +
    cut(OLD_EDGE, APP_LEFT, MID_Y) +
    connector(`M ${APP_RIGHT} ${MID_Y} H ${NEW_EDGE}`, NEW_EDGE, MID_Y, 0, C.green, false) +
    codePill((APP_RIGHT + NEW_EDGE) / 2, MID_Y, 'every save and read', C.green, C.greenBorder) +
    appWindow(APP_X, MID_Y, APP_W, APP_H) +
    drum(OLD_X, DRUM_TOP, DRUM_W, DRUM_H, { stroke: C.art, fill: C.card, width: 1.25, opacity: 0.4 }) +
    drum(NEW_X, DRUM_TOP, DRUM_W, DRUM_H, { stroke: C.green, fill: C.greenDeep, ink: C.green, lid: '#0A7A48' }) +
    glowDot(NEW_X, DRUM_TOP) +
    text(OLD_X, LABEL_Y, 18, 'Old database', { fill: C.muted, anchor: 'middle', tracking: '0' }) +
    badge(OLD_X, LABEL_Y + 14, 'Archived · kept 90 days', C.muted) +
    text(NEW_X, LABEL_Y, 18, 'Postgres', { fill: C.green, anchor: 'middle', tracking: '0' }) +
    customers('Nothing.'),
);

/* ------------------------------------------------------------------------------------------------ */
/* 8. If it goes wrong                                                                               */
/* ------------------------------------------------------------------------------------------------ */

/** The mouse pointer, tip at (x, y), outlined like the cursors on Supabase's realtime card. */
function pointer(x: number, y: number): string {
  const pts: [number, number][] = [
    [0, 0],
    [0, 21],
    [5, 16.5],
    [8.5, 24.5],
    [12, 23],
    [8.6, 15.2],
    [15, 15.2],
  ];
  const d = `M ${pts.map(([px, py]) => `${x + px} ${y + py}`).join(' L ')} Z`;
  return `<path d="${d}" fill="${C.ground}" stroke="${C.text}" stroke-width="1.5" stroke-linejoin="round"/>`;
}

/** One pill tab: the live one raised with a strong border and a green dot, the other quiet. */
function tab(x: number, y: number, w: number, h: number, label: string, live: boolean): string {
  const cy = y + h / 2;
  return live
    ? rect(x, y, w, h, C.raised, h / 2) +
        frame(x, y, w, h, white(0.5), h / 2) +
        glowDot(x + w / 2 - 42, cy) +
        text(x + w / 2 + 10, cy + 10, 28, label, { fill: C.text, font: MANROPE, weight: 500, anchor: 'middle', tracking: '0' })
    : frame(x, y, w, h, C.strong, h / 2) + text(x + w / 2, cy + 10, 28, label, { fill: C.muted, font: MANROPE, weight: 500, anchor: 'middle', tracking: '0' });
}

/** The setting itself, as a panel in the dashboard: the switch left, why it is safe right. */
function settingsPanel(): string {
  const y = 274;
  const h = 320;
  const bar = y + 40;
  const body = (bar + y + h) / 2;
  const split = 704;
  const tabY = body - 14;
  return (
    card(LEFT, y, WIDTH, h, C.dialog) +
    [0, 1, 2].map((i) => circle(LEFT + 22 + i * 12, y + 20, 3.5, white(0.16))).join('') +
    line(LEFT, bar + 0.5, RIGHT, bar + 0.5, C.border) +
    icon(ICON.database, IN_L, tabY - 48, C.light) +
    text(IN_L + 26, tabY - 35, 18, 'Reads from', { fill: C.text, font: MANROPE, weight: 600, tracking: '0' }) +
    tab(IN_L, tabY, 248, 72, 'Old', false) +
    tab(IN_L + 264, tabY, 248, 72, 'New', true) +
    pointer(IN_L + 158, tabY + 44) +
    line(split + 0.5, bar + 40, split + 0.5, y + h - 40, C.border) +
    icon(ICON.check, split + 48, body - 42, C.text) +
    text(split + 76, body - 29, 20, 'Practised in staging, twice', { fill: C.text, tracking: '0' }) +
    icon(ICON.check, split + 48, body + 22, C.text) +
    text(split + 76, body + 35, 20, 'Old database kept until week 6', { fill: C.text, tracking: '0' })
  );
}

const rollback = page(8, headline('One switch takes us back.') + settingsPanel());

export const DEV_SLIDES: DemoSlide[] = [
  { id: 'dev-1', title: 'Cover', svg: cover },
  { id: 'dev-2', title: 'Why we are moving', svg: why },
  { id: 'dev-3', title: 'The plan', svg: plan },
  { id: 'dev-4', title: 'Write to both', svg: writeBoth },
  { id: 'dev-5', title: 'Copy the past', svg: copyPast },
  { id: 'dev-6', title: 'Read from the new one', svg: readNew },
  { id: 'dev-7', title: 'Switch off the old one', svg: retire },
  { id: 'dev-8', title: 'If it goes wrong', svg: rollback },
];
